import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { api, getContent } from "@/lib/api";
import { Reveal, FadeIn, SectionTag } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import SEO from "@/components/SEO";

const SITE_URL =
  process.env.REACT_APP_SITE_URL ||
  (typeof window !== "undefined" ? window.location.origin : "https://catalystpetrochemllp.com");
const HOME_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Catalyst Petrochem LLP",
  url: SITE_URL,
  logo: `${SITE_URL}/logo-full.png`,
  description:
    "One of India's leading bitumen importers, supplying VG-10/30/40, PMB, CRMB, emulsions and specialty petrochemicals pan-India.",
  address: {
    "@type": "PostalAddress",
    streetAddress: "KD-197, Pitampura, Kohat Enclave Metro Station",
    addressLocality: "Delhi",
    postalCode: "110034",
    addressCountry: "IN",
  },
  telephone: ["+91-98712-94951", "+91-88006-53892"],
  email: "info@catalystpetrochemllp.com",
  sameAs: [],
};

function Hero({ hero }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section ref={ref} className="relative flex min-h-screen flex-col justify-end overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y }} className="absolute inset-0">
        <img src={hero.image} alt="Highway infrastructure at night" className="h-[120%] w-full object-cover opacity-55" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/55 to-[#0A0A0A]/25" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/70 via-transparent to-transparent" />
      </motion.div>
      <motion.div style={{ opacity: fade }} className="relative z-10 w-full px-6 pb-24 pt-24 md:px-12 lg:px-20">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 3.0, duration: 0.8 }}
          className="mb-8 flex items-center gap-3 font-mono text-[11px] tracking-[0.25em] text-white/60"
          data-testid="hero-kicker"
        >
          <span className="h-2 w-2 rotate-45 bg-brand" /> {hero.kicker}
        </motion.p>
        <h1
          className="font-display text-[8vw] font-bold uppercase leading-[1.08] tracking-normal sm:text-[7vw] lg:text-[5.25rem]"
          data-testid="hero-title"
        >
          {hero.lines.map((line, i) => (
            <Reveal key={line} immediate delay={3.1 + i * 0.14}>
              <span className={i === hero.accent_index ? "text-brand" : "text-white"}>{line}</span>
            </Reveal>
          ))}
        </h1>
        <FadeIn delay={3.7} className="mt-6 max-w-xl">
          <p className="text-base leading-relaxed text-neutral-400 md:text-lg" data-testid="hero-subtitle">
            {hero.subtitle}
          </p>
        </FadeIn>
        <FadeIn delay={3.85} className="mt-10 flex flex-wrap gap-4">
          <Link
            to="/contact"
            data-testid="hero-cta-quote"
            className="group inline-flex items-center gap-3 bg-brand px-8 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-brand-deep"
          >
            Request a Quote
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link
            to="/products"
            data-testid="hero-cta-products"
            className="inline-flex items-center gap-3 border border-white/20 px-8 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:border-brand hover:text-brand"
          >
            Explore Products
          </Link>
        </FadeIn>
      </motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 4.2 }}
        className="absolute bottom-8 right-6 z-10 hidden items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 md:flex lg:right-20"
      >
        Scroll
        <span className="h-10 w-px animate-pulse bg-brand/70" />
      </motion.div>
    </section>
  );
}

function Stats({ stats }) {
  return (
    <section className="border-y border-white/10" data-testid="stats-section">
      <div className="grid grid-cols-2 lg:grid-cols-4">
        {stats.map((s, i) => (
          <FadeIn
            key={s.label}
            delay={i * 0.08}
            className={`border-white/10 p-8 md:p-12 ${i % 2 === 1 ? "border-l" : ""} ${i > 1 ? "max-lg:border-t" : ""} lg:border-l lg:first:border-l-0`}
          >
            <p className="font-mono text-3xl font-medium text-white md:text-5xl" data-testid={`stat-value-${i}`}>
              {s.value}
              <span className="text-brand">{s.suffix}</span>
            </p>
            <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">{s.label}</p>
          </FadeIn>
        ))}
      </div>
    </section>
  );
}

function Manifesto({ about }) {
  return (
    <section className="px-6 py-24 md:px-12 md:py-32 lg:px-20" data-testid="manifesto-section">
      <div className="grid gap-14 md:grid-cols-12">
        <div className="md:col-span-4">
          <SectionTag index="01" label="The Company" />
          <Reveal className="mt-6">
            <h2 className="font-display text-4xl font-semibold uppercase leading-[1.08] tracking-normal sm:text-5xl sm:leading-[1.08]">
              Built on bitumen. Driven by trust.
            </h2>
          </Reveal>
          <FadeIn delay={0.2}>
            <p className="mt-8 max-w-sm text-sm leading-relaxed text-neutral-400">{about.intro}</p>
            <Link
              to="/about"
              data-testid="manifesto-more-link"
              className="mt-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-brand transition-colors hover:text-brand-light"
            >
              Read the full story <ArrowUpRight className="h-4 w-4" />
            </Link>
          </FadeIn>
        </div>
        <div className="md:col-span-8">
          {about.chapters.slice(0, 4).map((ch, i) => (
            <FadeIn key={ch.num} delay={i * 0.06}>
              <Link
                to="/about"
                data-testid={`chapter-link-${ch.num}`}
                className="group flex items-baseline gap-6 border-t border-white/10 py-7 transition-colors duration-300 last:border-b hover:bg-white/[0.02]"
              >
                <span className="font-mono text-sm text-brand">{ch.num}</span>
                <span className="font-display text-2xl uppercase tracking-tight text-white transition-colors duration-300 group-hover:text-brand md:text-3xl">
                  {ch.title}
                </span>
                <ArrowUpRight className="ml-auto h-5 w-5 shrink-0 text-neutral-600 transition-all duration-300 group-hover:translate-x-1 group-hover:text-brand" />
              </Link>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProductsPreview({ products }) {
  return (
    <section className="border-t border-white/10 bg-[#0C0C0C] px-6 py-24 md:px-12 md:py-32 lg:px-20" data-testid="products-preview">
      <div className="mb-16 flex flex-wrap items-end justify-between gap-8">
        <div>
          <SectionTag index="02" label="Product Range" />
          <Reveal className="mt-6">
            <h2 className="font-display text-4xl font-semibold uppercase leading-[1.08] tracking-normal sm:text-5xl sm:leading-[1.08]">
              From refinery to roadway.
            </h2>
          </Reveal>
        </div>
        <Link
          to="/products"
          data-testid="products-view-all"
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-brand transition-colors hover:text-brand-light"
        >
          All products <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>
      <div className="grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
        {products.slice(0, 6).map((p, i) => (
          <FadeIn key={p.id} delay={i * 0.05} className="h-full">
            <Link
              to={`/products/${p.slug}`}
              data-testid={`product-card-${p.slug}`}
              className="group flex h-full flex-col bg-[#0A0A0A] p-8 transition-colors duration-300 hover:bg-[#141414]"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">{p.category}</p>
              <h3 className="mt-4 font-display text-2xl font-medium uppercase tracking-normal text-white transition-colors duration-300 group-hover:text-brand">
                {p.name}
              </h3>
              <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-neutral-500">{p.description}</p>
              <div className="mt-auto flex items-center justify-between pt-8">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand">{p.standard}</span>
                <ArrowUpRight className="h-4 w-4 text-neutral-600 transition-all duration-300 group-hover:translate-x-1 group-hover:text-brand" />
              </div>
            </Link>
          </FadeIn>
        ))}
      </div>
    </section>
  );
}

function AchievementsPreview({ items }) {
  return (
    <section className="px-6 py-24 md:px-12 md:py-32 lg:px-20" data-testid="achievements-preview">
      <div className="mb-16 flex flex-wrap items-end justify-between gap-8">
        <div>
          <SectionTag index="03" label="Track Record" />
          <Reveal className="mt-6">
            <h2 className="font-display text-4xl font-semibold uppercase leading-[1.08] tracking-normal sm:text-5xl sm:leading-[1.08]">
              Recognised where it matters.
            </h2>
          </Reveal>
        </div>
        <Link
          to="/achievements"
          data-testid="achievements-view-all"
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-brand transition-colors hover:text-brand-light"
        >
          Full timeline <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>
      <div className="relative ml-2 border-l border-white/10 pl-8 md:ml-4 md:pl-12">
        {items.map((a, i) => (
          <FadeIn key={a.id} delay={i * 0.08} className="relative pb-14 last:pb-0">
            <span className="absolute -left-[41px] top-1.5 h-4 w-4 rotate-45 border border-brand bg-[#0A0A0A] shadow-[0_0_18px_rgba(255,87,34,0.55)] md:-left-[57px]" />
            <p className="font-mono text-xs tracking-[0.2em] text-brand">
              {a.year} · {a.tag}
            </p>
            <h3 className="mt-3 max-w-3xl font-display text-2xl font-medium uppercase tracking-normal md:text-3xl">{a.title}</h3>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-neutral-500">{a.description}</p>
          </FadeIn>
        ))}
      </div>
    </section>
  );
}

function CertificationsStrip({ certs }) {
  return (
    <section className="border-t border-white/10 px-6 py-20 md:px-12 lg:px-20" data-testid="certifications-strip">
      <div className="mb-12 flex flex-wrap items-end justify-between gap-8">
        <SectionTag index="04" label="Credentials" />
        <Link
          to="/certifications"
          data-testid="certifications-view-all"
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-brand transition-colors hover:text-brand-light"
        >
          All certifications <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>
      <div className="grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
        {certs.slice(0, 4).map((c, i) => (
          <FadeIn key={c.id} delay={i * 0.06} className="bg-[#0A0A0A] p-8">
            <p className="font-mono text-sm font-medium uppercase tracking-[0.15em] text-white">{c.name}</p>
            <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.15em] text-brand">{c.number}</p>
            <p className="mt-3 text-xs leading-relaxed text-neutral-500">{c.validity}</p>
          </FadeIn>
        ))}
      </div>
    </section>
  );
}

function BitumenTruck() {
  return (
    <svg
      viewBox="0 0 120 62"
      className="animate-truck-bob h-[4.2vw] max-h-14 w-auto min-h-8 drop-shadow-[0_0_14px_rgba(255,87,34,0.4)]"
      aria-hidden="true"
    >
      <rect x="8" y="14" width="72" height="26" rx="13" fill="#1d1d1d" stroke="#3a3a3a" strokeWidth="1.5" />
      <rect x="10" y="23" width="68" height="4" fill="#FF5722" opacity="0.9" />
      <circle cx="44" cy="27" r="5" fill="none" stroke="#3a3a3a" strokeWidth="1.5" />
      <path d="M84 18 h16 l11 12 v10 h-27 z" fill="#262626" stroke="#3a3a3a" strokeWidth="1.5" />
      <rect x="88" y="21.5" width="11" height="8" rx="1" fill="#0b0b0b" />
      <rect x="6" y="40" width="106" height="4" fill="#141414" />
      <circle cx="24" cy="47" r="7" fill="#0a0a0a" stroke="#4a4a4a" strokeWidth="1.5" />
      <circle cx="24" cy="47" r="2.8" fill="#2e2e2e" />
      <circle cx="42" cy="47" r="7" fill="#0a0a0a" stroke="#4a4a4a" strokeWidth="1.5" />
      <circle cx="42" cy="47" r="2.8" fill="#2e2e2e" />
      <circle cx="94" cy="47" r="7" fill="#0a0a0a" stroke="#4a4a4a" strokeWidth="1.5" />
      <circle cx="94" cy="47" r="2.8" fill="#2e2e2e" />
      <circle cx="106" cy="47" r="7" fill="#0a0a0a" stroke="#4a4a4a" strokeWidth="1.5" />
      <circle cx="106" cy="47" r="2.8" fill="#2e2e2e" />
      <circle cx="113" cy="33" r="2.4" fill="#FF8A65" />
      <path d="M13 40 L6 56" stroke="#050505" strokeWidth="5" strokeLinecap="round" />
      <ellipse cx="5" cy="58" rx="5" ry="2" fill="#050505" />
    </svg>
  );
}

function PavingWord({ word }) {
  return (
    <div className="pointer-events-none absolute bottom-3 left-0 w-full select-none" aria-hidden="true">
      <div className="relative mx-auto w-fit">
        <span className="text-outline block whitespace-nowrap font-display text-[12vw] font-bold uppercase leading-none lg:text-[9.5rem]">
          {word}
        </span>
        <div className="animate-pave-fill absolute inset-0 overflow-hidden">
          <span className="block whitespace-nowrap font-display text-[12vw] font-bold uppercase leading-none text-[#232323] lg:text-[9.5rem]">
            {word}
          </span>
          <span className="absolute right-0 top-0 h-full w-[3px] bg-brand/80 shadow-[0_0_18px_rgba(255,87,34,0.8)]" />
        </div>
        <div className="animate-pave-truck absolute -bottom-1">
          <BitumenTruck />
        </div>
      </div>
    </div>
  );
}

function CTA({ cta }) {
  return (
    <section className="relative overflow-hidden border-t border-white/10 px-6 pb-48 pt-28 md:px-12 md:pb-56 md:pt-40 lg:px-20" data-testid="cta-section">
      <div className="pointer-events-none absolute -top-40 left-1/2 h-96 w-[60rem] -translate-x-1/2 rounded-full bg-brand/10 blur-[120px]" />
      <PavingWord word="CATALYST" />
      <div className="relative">
        <SectionTag index="05" label="Work With Us" />
        <Reveal className="mt-8">
          <h2 className="max-w-5xl font-display text-5xl font-bold uppercase leading-[1.08] tracking-normal sm:text-6xl sm:leading-[1.08] lg:text-7xl lg:leading-[1.08]">
            {cta.title}
          </h2>
        </Reveal>
        <FadeIn delay={0.2}>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-neutral-400 md:text-lg">{cta.text}</p>
        </FadeIn>
        <FadeIn delay={0.35} className="mt-12">
          <Link
            to="/contact"
            data-testid="cta-contact-btn"
            className="group inline-flex items-center gap-3 bg-brand px-10 py-5 font-mono text-xs font-medium uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-brand-deep"
          >
            Start an enquiry
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </FadeIn>
      </div>
    </section>
  );
}

export default function Home() {
  const [content, setContent] = useState(null);
  const [products, setProducts] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [certs, setCerts] = useState([]);
  const [clients, setClients] = useState([]);

  useEffect(() => {
    getContent().then(setContent).catch(() => setContent({}));
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/achievements").then((r) => setAchievements(r.data.slice(0, 3))).catch(() => {});
    api.get("/certifications").then((r) => setCerts(r.data)).catch(() => {});
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  }, []);

  if (!content) return <div className="min-h-screen bg-[#0A0A0A]" data-testid="home-loading" />;

  const marqueeItems = [...products.map((p) => p.name), "Pan-India Supply"];

  return (
    <main data-testid="home-page">
      <SEO
        title="Catalyst Petrochem LLP — Bitumen Importer in India"
        description="One of India's leading bitumen importers. VG-10/30/40, PMB, CRMB, emulsions and specialty petrochemicals supplied pan-India from the Middle East."
        path="/"
        jsonLd={HOME_JSON_LD}
      />
      <Hero hero={content.hero} />
      {marqueeItems.length > 1 && <Marquee items={marqueeItems} />}
      <Stats stats={content.stats} />
      <Manifesto about={content.about} />
      <ProductsPreview products={products} />
      <AchievementsPreview items={achievements} />
      <CertificationsStrip certs={certs} />
      {clients.length > 0 && (
        <Marquee items={clients.map((c) => c.name)} testId="clients-marquee" className="border-b-0" />
      )}
      <CTA cta={content.cta} />
    </main>
  );
}
